import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SalesOrdersList from './SalesOrdersList';
import ViewSalesOrder from './ViewSalesOrder';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import './SalesOrders.css';

function SalesOrders({ initialOrderId }) {
  const { t } = useTranslation('sales');
  const navigate = useNavigate();
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'view', 'edit', 'create'

  // Handle initial order ID from cross-module navigation
  useEffect(() => {
    if (initialOrderId) {
      setSelectedOrderId(parseInt(initialOrderId));
      setViewMode('view');
    }
  }, [initialOrderId]);

  useEffect(() => {
    // Set toolbar based on current mode
    if (window.setToolbar) {
      if (viewMode === 'list') {
        window.setToolbar({
          title: t('salesOrders.toolbar.titles.salesOrders'),
          buttons: [
            {
              label: t('salesOrders.toolbar.buttons.newSalesOrder'),
              icon: '➕',
              onClick: handleNew,
              variant: 'primary'
            },
            {
              label: t('salesOrders.toolbar.buttons.refresh'),
              icon: '🔄',
              onClick: handleRefresh
            }
          ]
        });
      } else if (viewMode === 'view') {
        window.setToolbar({
          title: t('salesOrders.toolbar.titles.viewSalesOrder'),
          showBack: true,
          onBack: handleBack,
          buttons: [
            {
              label: t('salesOrders.toolbar.buttons.edit'),
              icon: '✏️',
              onClick: handleEdit,
              variant: 'secondary'
            },
            {
              label: t('salesOrders.toolbar.buttons.delete'),
              icon: '🗑️',
              onClick: handleDelete,
              variant: 'danger'
            }
          ]
        });
      } else if (viewMode === 'edit') {
        window.setToolbar({
          title: t('salesOrders.toolbar.titles.editSalesOrder'),
          showBack: false,
          buttons: [
            {
              label: t('salesOrders.toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('salesOrders.toolbar.buttons.save'),
              icon: '💾',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      } else if (viewMode === 'create') {
        window.setToolbar({
          title: t('salesOrders.toolbar.titles.newSalesOrder'),
          showBack: false,
          buttons: [
            {
              label: t('salesOrders.toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('salesOrders.toolbar.buttons.create'),
              icon: '➕',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      }
    }

    return () => {
      if (window.setToolbar) {
        window.setToolbar(null);
      }
    };
  }, [viewMode]);

  const handleSelectOrder = (orderId) => {
    setSelectedOrderId(orderId);
    setViewMode('view');
  };

  const handleNew = () => {
    setSelectedOrderId(null);
    setViewMode('create');
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleDelete = () => {
    // Delete will be handled in ViewSalesOrder component
    if (window.viewSalesOrderRef && window.viewSalesOrderRef.handleDelete) {
      window.viewSalesOrderRef.handleDelete();
    }
  };

  const handleSave = () => {
    // Save will be handled in ViewSalesOrder component
    if (window.viewSalesOrderRef && window.viewSalesOrderRef.handleSave) {
      window.viewSalesOrderRef.handleSave();
    }
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedOrderId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedOrderId(null);
  };

  const handleRefresh = () => {
    // Trigger refresh in SalesOrdersList
    if (window.salesOrdersListRef && window.salesOrdersListRef.fetchOrders) {
      window.salesOrdersListRef.fetchOrders();
    }
  };

  const handleOrderSaved = (order) => {
    setSelectedOrderId(order.sales_order_id);
    setViewMode('view');
    // Refresh the list
    if (window.salesOrdersListRef && window.salesOrdersListRef.fetchOrders) {
      window.salesOrdersListRef.fetchOrders();
    }
  };

  return (
    <div className="sales-orders-page">
      <ResizablePanels
        leftPanel={
          <SalesOrdersList
            ref={ref => window.salesOrdersListRef = ref}
            onSelectOrder={handleSelectOrder}
            selectedOrderId={selectedOrderId}
            onNew={handleNew}
          />
        }
        rightPanel={
          viewMode !== 'list' ? (
            <ViewSalesOrder
              ref={ref => window.viewSalesOrderRef = ref}
              orderId={selectedOrderId}
              onBack={handleBack}
              editMode={viewMode === 'edit'}
              createMode={viewMode === 'create'}
              onSave={handleOrderSaved}
              onCancel={handleCancel}
              onEdit={handleEdit}
            />
          ) : null
        }
        leftCollapsed={viewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={30}
        maxLeftWidth={70}
      />
    </div>
  );
}

export default SalesOrders;